import { useState , useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Box,
  Typography,
  Avatar,
  TextField
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Delete, CloudUpload } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { t } from 'i18next';

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    staff: string;
    title: string;
    description: string;
    maintenanceFromDate: string;
    maintenanceToDate: string;
    attachments: File[];
  }) => void;
  additionalData: {
    stationName: string
  };
}

const MaintenanceRecordModal = ({ open, onClose, onSubmit , additionalData}: ImageUploadModalProps) => {
  const [location, setLocation] = useState<string>(additionalData.stationName);
  const [staff, setStaff] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedFromDateTime, setSelectedFromDateTime] = useState<Date | null>(null);
  const [selectedToDateTime, setSelectedToDateTime] = useState<Date | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dateError, setDateError] = useState<string | null>(null);


  // Validate date range
  useEffect(() => {
    if (selectedFromDateTime && selectedToDateTime) {
      if (selectedToDateTime < selectedFromDateTime) {
        setDateError(t('ToDateBeforeFromDateError'));
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null); // Clear error if either date is unset
    }
  }, [selectedFromDateTime, selectedToDateTime]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': [],
      'application/pdf': [], // Accept PDFs
      'application/doc': [], // Accept Word docs
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // Accept Word docs
      'application/vnd.ms-excel': [], // Accept Excel files
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [] // Accept Excel files
    },
    onDrop: (acceptedFiles) => {
      setFiles([...files, ...acceptedFiles]);
    }
  });

  const handleSubmit = () => {
    if (selectedFromDateTime && selectedToDateTime && title && staff) {
      onSubmit({
        staff,
        title,
        description,
        maintenanceFromDate: selectedFromDateTime.toISOString(),
        maintenanceToDate: selectedToDateTime.toISOString(),
        attachments: files
      });
      handleClose();
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const handleClose = () => {
    setStaff('');
    setTitle('');
    setDescription('');
    setSelectedFromDateTime(null);
    setSelectedToDateTime(null);
    setFiles([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Add Maintenance Record')}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ mt: 2 }}>
          <TextField
              variant="filled"
              label={t('Location')}
              value={location}
              fullWidth
              disabled
              sx={{ mb: 2 }}
            />
          <TextField
              label={t('Staff')}
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <DateTimePicker
              label={`${t('Select Maintenance From Date')}*`}
              value={selectedFromDateTime}
              onChange={(newValue) => setSelectedFromDateTime(newValue)}
              sx={{ width: '100%', mb: 2 }}
            />
            <DateTimePicker
              label={`${t('Select Maintenance To Date')}*`}
              value={selectedToDateTime}
              minDateTime={selectedFromDateTime}
              onChange={(newValue) => setSelectedToDateTime(newValue)}
              sx={{ width: '100%', mb: 2 }}
            />
          </Box>
        </LocalizationProvider>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('Upload Attachments (Multiple allowed)')}
          </Typography>

          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main'
                }
              }}
            >
              <CloudUpload fontSize="large" color="action" />
              <Typography>{t('Drag & drop files here, or click to select')}</Typography>
            </Box>
          </div>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {files.map((file, index) => (
              <Grid item xs={4} sm={3} md={2} key={index}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    variant="rounded"
                    src={URL.createObjectURL(file)}
                    sx={{ width: 100, height: 100 }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      color: 'error.main',
                      backgroundColor: 'background.paper'
                    }}
                    onClick={() => handleRemoveFile(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}> {t('Cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!!dateError || !selectedFromDateTime || !selectedToDateTime || !title || !staff}
        >
          {t('Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceRecordModal;